import type { SmartStatus } from '@/types'

/**
 * Progress (0–100) through the production cycle month, based on today's date.
 * The cycle is the calendar month in which the client is expected to be
 * producing content (normally the month BEFORE the selected content month).
 */
export function getCycleProgress(
  cycleMonth: number,
  cycleYear: number,
  today: Date = new Date()
): number {
  const daysInCycle = new Date(cycleYear, cycleMonth + 1, 0).getDate()
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth()

  if (todayYear < cycleYear || (todayYear === cycleYear && todayMonth < cycleMonth)) {
    return 0
  }
  if (todayYear > cycleYear || (todayYear === cycleYear && todayMonth > cycleMonth)) {
    return 100
  }

  return Math.round((today.getDate() / daysInCycle) * 100)
}

// ─── Thresholds ────────────────────────────────────────────────────────────

const AHEAD_DELTA = 20
const BEHIND_DELTA = -15
const LATE_BEHIND_DELTA = -20
const LATE_CYCLE_PROGRESS = 70
const CRITICAL_CYCLE_PROGRESS = 90
const CRITICAL_PERCENTAGE = 50

/**
 * Resolves the single-word production status for a client, weighing the
 * done percentage against how far the production cycle has progressed.
 */
export function getSmartStatus(
  percentage: number,
  totalCards: number,
  cycleProgress: number
): SmartStatus {
  if (totalCards > 0 && percentage >= 100) return 'finalizado'

  const delta = percentage - cycleProgress

  if (cycleProgress >= CRITICAL_CYCLE_PROGRESS && percentage < CRITICAL_PERCENTAGE) {
    return 'critico'
  }
  if (cycleProgress >= LATE_CYCLE_PROGRESS && delta <= LATE_BEHIND_DELTA) {
    return 'atrasado'
  }
  if (delta >= AHEAD_DELTA) return 'adiantado'
  if (delta <= BEHIND_DELTA) return 'atencao'
  return 'bom'
}

// ─── Display ─────────────────────────────────────────────────────────────

export const SMART_STATUS_LABELS: Record<SmartStatus, string> = {
  finalizado: 'Finalizado',
  adiantado: 'Adiantado',
  bom: 'Bom',
  atencao: 'Atenção',
  atrasado: 'Atrasado',
  critico: 'Crítico',
}

export const SMART_STATUS_CLASSES: Record<SmartStatus, string> = {
  finalizado: 'bg-green-50 text-green-700 border border-green-200',
  adiantado: 'bg-blue-50 text-blue-700 border border-blue-200',
  bom: 'bg-teal-50 text-teal-700 border border-teal-200',
  atencao: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  atrasado: 'bg-orange-50 text-orange-700 border border-orange-200',
  critico: 'bg-red-50 text-red-600 border border-red-200',
}
